import { ComponentProps, FC, useEffect, useState } from 'react';
import {
  AutocompleteElement,
  useFieldArray,
  useFormContext,
  useWatch,
} from 'react-hook-form-mui';

import { getProductionAbilitiesCategories } from '../../../../../services/companyService';
import { Stack } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { pickStringWithLocale } from '../../../../../utils/pickStringWithLocale';

export const TaxonomyAutocomplete: FC<
  Omit<ComponentProps<typeof AutocompleteElement>, 'options'> & {
    disabled?: boolean;
  }
> = (props) => {
  const router = useRouter();
  const locale = router.locale;
  const { t } = useTranslation(['directory', 'common']);
  const { control, setValue } = useFormContext();
  const values = useWatch();
  const [options, setOptions] = useState<
    {
      id: string;
      label: string;
    }[][]
  >([]);

  const val = values[props.name];

  const { fields } = useFieldArray({
    control,
    name: props.name,
  });

  useEffect(() => {
    async function initializeOptions() {
      const categoriesToFetch = val || [];
      const categoriesToFetchFiltered = categoriesToFetch.filter(
        (c) => c !== '-1' && c !== undefined && c !== null && c !== ''
      );

      const promises = [
        getProductionAbilitiesCategories(null),
        ...categoriesToFetchFiltered.map((r) => {
          return getProductionAbilitiesCategories(r);
        }),
      ];

      const options = (await Promise.all(promises)).map((r) => r.data);
      const optionsForAutocomplete = options.map((o) =>
        o.map((x) => ({
          id: String(x.id),
          label: pickStringWithLocale(locale, 'name', x),
        }))
      );

      setOptions(optionsForAutocomplete);
    }

    initializeOptions();
  }, [val, props.name, setValue]);

  const handleOnChange = (e, value, index) => {
    if (value === null) {
      setValue(props.name, [...val.slice(0, index), '-1']);
      return;
    }

    setValue(props.name, [...val.slice(0, index), value?.id, '-1']);
  };

  return (
    <Stack spacing={1}>
      {fields.map((r, index) => {
        const fieldOptions = options[index] || [];

        const label = (options[index - 1] || []).find(
          (x) => x.id === val[index - 1]
        )?.label;

        return (
          <Field
            key={r.id}
            fieldOptions={fieldOptions}
            index={index}
            handleOnChange={handleOnChange}
            name={props.name}
            label={
              label
                ? `${t('common:panel:filterCategories', { name: label })}`
                : t('common:panel:productCategories')
            }
            disabled={props.disabled}
          />
        );
      })}
    </Stack>
  );
};

const Field = ({
  name,
  label,
  index,
  fieldOptions,
  handleOnChange,
  disabled,
}) => {
  if (fieldOptions?.length === 0) return null;

  return (
    <AutocompleteElement
      name={`${name}[${index}]`}
      label={label}
      options={fieldOptions}
      autocompleteProps={{
        onChange: (e, value) => handleOnChange(e, value, index),
        getOptionLabel: (option) => {
          if (option === '-1') return '';

          if (option?.id) {
            return option.label;
          }

          return fieldOptions.find((x) => x.id === option)?.label;
        },
        disabled: disabled,
      }}
    />
  );
};
